import { Box, IconButton, Tooltip } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { CustomChip } from 'src/shared/components/organisms/customChip';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { getContactUnsubscriptions, deleteContactUnsubscription } from 'src/shared/services/groupMessageService';
import { ContactUnsubscriptionResponse } from '../models/contactUnsubscriptionResponse';

type ContactUnsubscriptionListProps = {
  searchString: string | undefined;
  type: 'email' | 'sms';
};

export const ContactUnsubscriptionList = ({ searchString, type }: ContactUnsubscriptionListProps) => {
  const { t } = useTranslation(['groupMessage']);
  const isMounted = useIsMounted();
  const [rows, setRows] = useState<(ContactUnsubscriptionResponse & { id: number })[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'unsubscribedAt', sort: 'desc' }]);
  const [pendingDeleteValue, setPendingDeleteValue] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const prevSearch = useRef(searchString);

  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: [10, 25, 50, 100],
    pageSize: 25,
    page: 0,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    if (prevSearch.current !== searchString) {
      prevSearch.current = searchString;
      updateData('page', 0);
    }
  }, [searchString]);

  useEffect(() => {
    const fetchData = async () => {
      updateData('loading', true);
      try {
        const sortCol = sortModel[0]?.field ?? 'unsubscribedAt';
        const sortDir = sortModel[0]?.sort ?? 'desc';
        const result = await getContactUnsubscriptions(data.page, data.pageSize, searchString, type, sortCol, sortDir);
        if (isMounted()) {
          const mappedRows = result.data.results.map((item, i) => ({
            ...item,
            id: data.page * data.pageSize + i,
          }));
          setRows(mappedRows);
          updateData('totalRows', result.data.totalItems);
        }
      } catch (error) {
        if (isMounted()) ConsoleHelper.log(error);
      } finally {
        if (isMounted()) updateData('loading', false);
      }
    };

    fetchData();
  }, [data.page, data.pageSize, searchString, sortModel, refreshKey, isMounted]);

  const handleDeleteConfirm = async (result?: { id: string }) => {
    if (!result) {
      setPendingDeleteValue(null);
      return;
    }
    try {
      await deleteContactUnsubscription(result.id, type);
      if (isMounted()) {
        setRefreshKey((prev) => prev + 1);
      }
    } catch (error) {
      if (isMounted()) ConsoleHelper.log(error);
    } finally {
      if (isMounted()) setPendingDeleteValue(null);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'value',
      headerName: t('groupMessage:unsubscribed.tableHeaders.value'),
      flex: 2,
      minWidth: 180,
    },
    {
      field: 'type',
      headerName: t('groupMessage:unsubscribed.tableHeaders.type'),
      flex: 1,
      minWidth: 100,
      renderCell: (params) => {
        const val = params.row.type as string;
        return <>{val === 'email' ? t('groupMessage:unsubscribed.filterEmail') : 'SMS'}</>;
      },
    },
    {
      field: 'unsubscribedAt',
      headerName: t('groupMessage:unsubscribed.tableHeaders.unsubscribedAt'),
      flex: 1,
      minWidth: 130,
      renderCell: (params) => {
        const val = params.row.unsubscribedAt as string;
        return <>{val ? new Date(val).toLocaleDateString('sv-SE') : ''}</>;
      },
    },
    {
      field: 'source',
      headerName: t('groupMessage:unsubscribed.tableHeaders.source'),
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'memberName',
      headerName: t('groupMessage:unsubscribed.tableHeaders.member'),
      flex: 1,
      minWidth: 140,
      renderCell: (params) => {
        const row = params.row as ContactUnsubscriptionResponse;
        if (row.memberCount > 1 || row.multipleAssociations) {
          return <CustomChip label={t('groupMessage:unsubscribed.multipleAssociationsLabel')} color='warning' size='small' smallRadius />;
        }
        return <>{row.memberName ?? ''}</>;
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 56,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row as ContactUnsubscriptionResponse;
        const noMemberReason = row.memberCount === 0 ? t('groupMessage:unsubscribed.deleteDisabledNoMember') : null;
        const disabledReason = row.multipleAssociations ? t('groupMessage:unsubscribed.deleteDisabledMultipleAssociations') : noMemberReason;
        const isDisabled = !row.canDelete;
        return (
          <Tooltip title={disabledReason ?? ''} disableHoverListener={!isDisabled}>
            <span>
              <IconButton size='small' disabled={isDisabled} onClick={() => setPendingDeleteValue(row.value)}>
                <Delete fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Box sx={{ pt: 1 }}>
      <DataGrid<ContactUnsubscriptionResponse & { id: number }>
        columns={columns}
        rows={rows}
        dataModel={data}
        disableSelectionOnClick
        checkboxSelection={false}
        selectionModel={[]}
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onSelectionModelChange={() => {}}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          setSortModel(newSortModel);
          updateData('page', 0);
        }}
        onPageChange={(newPage) => updateData('page', newPage)}
        onPageSizeChange={(newSize) => {
          updateData('page', 0);
          updateData('pageSize', newSize);
        }}
      />
      {pendingDeleteValue !== null && (
        <DeleteConfirmationDialog
          open
          title={t('groupMessage:unsubscribed.deleteTitle')}
          modaltext={t('groupMessage:unsubscribed.deleteWarning')}
          value={pendingDeleteValue}
          confirmButtonText={t('groupMessage:unsubscribed.deleteConfirmButton')}
          confirmButtonColor='error'
          onClose={handleDeleteConfirm}
        />
      )}
    </Box>
  );
};
